import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import { GridItemChangeEvent } from '@progress/kendo-react-grid';
import { TextArea, TextAreaChangeEvent } from '@progress/kendo-react-inputs';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import useAdjustBalanceColumn from 'src/assets/static/view_checkbook/useAdjustBalanceColumn';
import Button from 'src/components/design-system/Button';
import Dialog from 'src/components/design-system/Dialog';
import PopUp from 'src/components/design-system/PopUp';
import GridWrapper from 'src/components/GridWrapper';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import useCompany from 'src/hooks/useCompany';
import useDebounce from 'src/hooks/useDebounce';
import useViewCheckbook from 'src/hooks/useViewCheckbook';
import { GLCodePeriodBudgetResponse } from 'src/services/useGlCodePeriodBudgetApi';
import useJournalEntryApi from 'src/services/useJournalEntryApi';
import { SetState } from 'src/types/generic';

const DATA_ITEM_KEY: string = 'rowId';

const defaultProps = {
  DataItemKey: DATA_ITEM_KEY,
  sortable: false,
  resizable: true,
  showColumnMenu: false,
} as const;

interface JournalEntryDialogProps {
  open: boolean;
  setOpen: SetState<boolean>;
  dataItem: Record<string, any>;
}

const JournalEntryDialog: FC<JournalEntryDialogProps> = ({ open, setOpen, dataItem }) => {
  const { isGlCodesBudgetLoading } = useViewCheckbook();
  const { t } = useTranslation();
  const {
    company: { companyId },
  } = useCompany();
  const {
    getGlCodePeriodBudget,
    getGLPeriods,
    glPeriodIdByPeriod,
    setEditingGlCodePeriodBudget,
    setOriginalGlCodePeriodBudget,
  } = useViewCheckbook();

  const [inputTextBox, setInputTextBox] = useState<string>('');
  const [adjustBalanceData, setAdjustBalanceData] = useState<GLCodePeriodBudgetResponse[]>([]);
  const debouncedAdjustBalanceData = useDebounce(adjustBalanceData, 1000);

  const { journalEntry, journalEntryLoading } = useJournalEntryApi(
    debouncedAdjustBalanceData?.[0]?.id?.toString(),
    companyId
  );
  const cancelAdjustBalance = useCallback(() => {
    setOpen((prev) => !prev);
    setAdjustBalanceData([]);
    setInputTextBox('');
  }, [setOpen]);

  useEffect(() => {
    if (open) {
      setAdjustBalanceData([
        {
          ...dataItem,
          endingBalance: dataItem.remainingBudgetAmount,
        } as GLCodePeriodBudgetResponse,
      ]);
    }
  }, [dataItem, open]);

  const adjustBalanceColumn = useAdjustBalanceColumn();
  const handleItemChange = useCallback(
    (e: GridItemChangeEvent) => {
      const { field = '', value } = e;
      setAdjustBalanceData((prev) => [{ ...prev[0], [field]: value }]);
    },
    [setAdjustBalanceData]
  );

  const updateAdjustBalance = useCallback(async () => {
    const newAmount = adjustBalanceData?.[0]?.newAmount || 0;
    try {
      const result = await journalEntry({
        amount: adjustBalanceData?.[0]?.card === 'subtract' ? -newAmount : newAmount,
        description: inputTextBox,
        glPeriodId: glPeriodIdByPeriod,
      });
      cancelAdjustBalance();
      if (result) {
        toast.success(t('Adjustment updated successfully'));
        await getGLPeriods();
        const res = await getGlCodePeriodBudget(glPeriodIdByPeriod);
        const updatedGlCode = res.find(
          (glCode) => glCode.id === adjustBalanceData?.[0]?.id
        ) as GLCodePeriodBudgetResponse;
        const { spentAmount, hasSpentTransactionData, id } = updatedGlCode;
        setEditingGlCodePeriodBudget((prev) =>
          prev.map((item) =>
            item.id === id
              ? {
                  ...item,
                  spentAmount,
                  remainingBudgetAmount: item.budgetAmount - spentAmount,
                  hasSpentTransactionData,
                }
              : item
          )
        );
        setOriginalGlCodePeriodBudget((prev) =>
          prev.map((item) =>
            item.id === id
              ? {
                  ...item,
                  spentAmount,
                  remainingBudgetAmount: item.budgetAmount - spentAmount,
                  hasSpentTransactionData,
                }
              : item
          )
        );
      }
    } catch (e) {
      console.error(e);
    }
  }, [
    adjustBalanceData,
    cancelAdjustBalance,
    getGLPeriods,
    getGlCodePeriodBudget,
    glPeriodIdByPeriod,
    inputTextBox,
    journalEntry,
    setEditingGlCodePeriodBudget,
    setOriginalGlCodePeriodBudget,
    t,
  ]);

  const adjustBalanceErrors = useMemo(() => {
    const errors = { isPriceError: false, isTextError: false, isTextCountError: false };

    if (debouncedAdjustBalanceData.length > 0 && debouncedAdjustBalanceData[0].newAmount <= 0) {
      errors.isPriceError = true;
    }
    if (!inputTextBox) {
      errors.isTextError = true;
    }
    if (inputTextBox.length > 1000) {
      errors.isTextCountError = true;
    }

    return errors;
  }, [debouncedAdjustBalanceData, inputTextBox]);

  const updateErrorMessages: string[] = useMemo(() => {
    let errorMessages: string[] = [];
    if (adjustBalanceErrors.isPriceError) {
      errorMessages.push(t('Credit/Debit Adjustment must be a positive value.'));
    }
    if (adjustBalanceErrors.isTextError) {
      errorMessages.push(t('A reason for adjustment must be added.'));
    }
    if (adjustBalanceErrors.isTextCountError) {
      errorMessages.push(
        t('Character Limit for Notes long textbook should not be more than 1000.')
      );
    }
    return errorMessages;
  }, [
    adjustBalanceErrors.isPriceError,
    adjustBalanceErrors.isTextCountError,
    adjustBalanceErrors.isTextError,
    t,
  ]);

  const isUpdateButtonDisabled =
    adjustBalanceErrors.isPriceError ||
    adjustBalanceErrors.isTextError ||
    adjustBalanceErrors.isTextCountError;

  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);

  const handleTextAreaChange = useCallback(
    (e: TextAreaChangeEvent) => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      const newTimeoutId = setTimeout(() => {
        setInputTextBox(e.value);
      }, 500);
      setTimeoutId(newTimeoutId);
    },
    [timeoutId]
  );

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      width="85%"
      id="modal"
      title={t('Adjust Checkbook Balance')}
    >
      <GridWrapper
        id="adjustBalanceGrid"
        columns={adjustBalanceColumn}
        dataSource={adjustBalanceData}
        onItemChange={handleItemChange}
        minHeight="70px"
        loading={isGlCodesBudgetLoading}
        cellClick={(e) => ({ updateErrorMessages, adjustBalanceErrors, setAdjustBalanceData })}
        {...defaultProps}
      />
      <div className="mt-1">
        <p id="modal">Please enter the reason for your checkbook balance adjustment:</p>
        <div className="m-1">
          <TextArea
            valid={!adjustBalanceErrors.isTextError && !adjustBalanceErrors.isTextCountError}
            onChange={handleTextAreaChange}
            className="h-[80px]"
            autoSize={true}
            rounded={null}
            fillMode={'outline'}
            style={{ overflow: 'auto', resize: 'none' }}
          />
        </div>
      </div>
      <DialogActionsBar>
        <div className="flex justify-end">
          <PopUp
            popupAlign={{ horizontal: 'center', vertical: 'bottom' }}
            anchorAlign={{ horizontal: 'right', vertical: 'top' }}
            className="max-h-[180px] "
            content={
              <div className="m-1 max-w-[350px]">
                <PopUpContentWrapper type="Error" contents={updateErrorMessages} />
              </div>
            }
            show={isUpdateButtonDisabled}
          >
            <Button
              onClick={updateAdjustBalance}
              color="success"
              loading={journalEntryLoading}
              disabled={isUpdateButtonDisabled}
            >
              Update
            </Button>
          </PopUp>
          <Button onClick={cancelAdjustBalance} color="secondary">
            Cancel
          </Button>
        </div>
      </DialogActionsBar>
    </Dialog>
  );
};

export default JournalEntryDialog;
